package org.example.newportfolio.models

import org.example.newportfolio.utils.Res

data class ProductFeature(
    val icon: String,   // Emoji icon
    val title: String,
    val description: String,
)

enum class Product(
    val productName: String,
    val tagline: String,
    val description: String,
    val detailedDescription: String,
    val link: String,
    val imageRes: String,
    val techStack: List<String>,
    val features: List<ProductFeature>,
    val status: String = "In Development",
    val detailImages: List<String> = emptyList(),
) {
    PostSyncPro(
        productName = "PostSync Pro",
        tagline = "AI-Powered QA Automation",
        description = "AI-Powered QA Automation Desktop App. Merges Postman/Swagger specs and auto-generates fuzz tests.",
        detailedDescription = "PostSync Pro is an intelligent desktop application designed to revolutionize API testing workflows. " +
                "It automatically imports and merges API definitions from Postman Collections and Swagger/OpenAPI specifications, " +
                "then leverages AI to generate comprehensive fuzz tests, edge-case scenarios, and regression test suites. " +
                "Built for QA engineers and developers who need to ensure API reliability at scale without writing hundreds of test cases manually.",
        link = "https://github.com/MohamedElgohary88",
        imageRes = Res.Images.POST_SYNC,
        techStack = listOf("Kotlin", "Compose Desktop", "Ktor Client", "OpenAI API", "SQLDelight", "Koin", "Coroutines"),
        status = "Beta",
        features = listOf(
            ProductFeature(
                icon = "📥",
                title = "Smart Import & Merge",
                description = "Automatically import API specs from Postman Collections (v2.1) and Swagger/OpenAPI (3.x). " +
                        "Intelligently merge duplicate endpoints, resolve conflicts, and create a unified API catalog."
            ),
            ProductFeature(
                icon = "🤖",
                title = "AI-Powered Test Generation",
                description = "Leverage advanced AI models to auto-generate comprehensive fuzz tests, boundary value tests, " +
                        "and edge-case scenarios for every endpoint. Covers positive, negative, and security test cases."
            ),
            ProductFeature(
                icon = "⚡",
                title = "Batch Execution Engine",
                description = "Run hundreds of generated tests in parallel with configurable concurrency. " +
                        "Real-time progress tracking, automatic retries for flaky tests, and detailed execution logs."
            ),
            ProductFeature(
                icon = "📊",
                title = "Smart Reporting & Analytics",
                description = "Generate detailed HTML and PDF test reports with pass/fail breakdown, response time analytics, " +
                        "coverage metrics, and trend analysis across test runs."
            ),
            ProductFeature(
                icon = "🔐",
                title = "Security Scanning",
                description = "Built-in security test patterns for SQL injection, XSS, authentication bypass, " +
                        "and OWASP Top 10 vulnerabilities. Automatically flags potential security risks in API responses."
            ),
            ProductFeature(
                icon = "🔄",
                title = "Environment Management",
                description = "Manage multiple environments (Dev, Staging, Production) with variable substitution, " +
                        "authentication token management, and environment-specific configurations."
            ),
            ProductFeature(
                icon = "📁",
                title = "Project Workspace",
                description = "Organize tests into workspaces with version history, team sharing capabilities, " +
                        "and Git-friendly export formats. Never lose a test configuration again."
            ),
            ProductFeature(
                icon = "🎯",
                title = "Coverage Analysis",
                description = "Visualize API endpoint coverage with interactive charts. Identify untested endpoints, " +
                        "missing HTTP methods, and gaps in your test suite at a glance."
            )
        )
    ),
    VariantCast(
        productName = "VariantCast",
        tagline = "Variant-Level Collection Merchandising Router",
        description = "Show specific product variants in specific collections — without duplicating products or splitting inventory.",
        detailedDescription = "VariantCast is a Shopify app that lets merchants on Basic through Advanced plans display specific product variants in specific collections — a feature locked behind Shopify Plus ($2,000/month). Uses Product Metafields (JSON) to store variant-to-collection mappings and an App Embed Block (<1KB JS) to dynamically render the correct variant thumbnail, price, and URL on the storefront. Features a 3-layer webhook loop prevention system using Redis for idempotency, write-stamp tracking, and atomic processing locks. Built with a theme-agnostic DOM walker that works on all Online Store 2.0 themes with zero code edits.",
        link = "https://github.com/MohamedElgohary88",
        imageRes = Res.Images.VARIANT_CAST,
        techStack = listOf("Remix", "TypeScript", "React", "Polaris", "Prisma", "PostgreSQL", "Redis (Upstash)", "Shopify GraphQL API", "Theme App Extensions", "Sentry"),
        status = "Pre-Launch",
        detailImages = listOf(Res.Images.VARIANT_CAST_1, Res.Images.VARIANT_CAST_2),
        features = listOf(
            ProductFeature(
                icon = "🎯",
                title = "Variant-to-Collection Mapping",
                description = "Three-step Polaris UI: select a product, pick a variant, choose target collections. Maps are stored as JSON Metafields on the product for zero-latency storefront access."
            ),
            ProductFeature(
                icon = "⚡",
                title = "App Embed DOM Swap",
                description = "A <1KB synchronous JavaScript snippet injects into the collection page <head>, swaps the product image, price, and URL on DOMContentLoaded. Theme-agnostic: works on all OS 2.0 themes."
            ),
            ProductFeature(
                icon = "🛡️",
                title = "3-Layer Webhook Loop Prevention",
                description = "Prevents infinite webhook loops via Redis-backed idempotency deduplication (24h TTL), write-stamp tracking (60s TTL), and field-level diffing. Atomic processing locks prevent race conditions."
            ),
            ProductFeature(
                icon = "💳",
                title = "Free + Pro Billing",
                description = "Free tier: 10 active mappings. Pro tier: $29/month, 14-day free trial, unlimited mappings. Uses Shopify's native recurring charge billing API."
            ),
            ProductFeature(
                icon = "📋",
                title = "Mapping Dashboard",
                description = "Full CRUD management with pagination, search, bulk delete, and per-mapping status. Product and collection browsers with variant-level detail and mapping badges."
            ),
            ProductFeature(
                icon = "🔁",
                title = "Automatic Cache Updates",
                description = "Listens to products/update and collections/update webhooks to auto-refresh cached prices, images, and titles. Handles collection renames gracefully by re-keying all affected Metafields."
            )
        )
    ),
    AiDescriptions(
        productName = "AI Descriptions",
        tagline = "AI-Powered Product Description Generator",
        description = "Generate high-converting product descriptions, SEO titles, and meta descriptions using OpenAI.",
        detailedDescription = "AI Descriptions is a Shopify app that uses OpenAI's GPT-4o-mini to write compelling product descriptions, SEO titles, and meta descriptions directly inside the Shopify admin. Features a sophisticated prompt engineering system with 5 tones (Professional, Casual, Playful, Luxury, Minimalist) and 3 length options (Short, Medium, Long). Includes a custom OpenAI client with exponential backoff with jitter (4 retries, 90s total budget), safe error message sanitization, per-generation cost tracking, and one-click apply to Shopify via GraphQL Admin API.",
        link = "https://github.com/MohamedElgohary88",
        imageRes = Res.Images.AI_DESCRIPTIONS,
        techStack = listOf("Remix", "TypeScript", "React", "Polaris", "Prisma", "PostgreSQL", "OpenAI API", "Shopify GraphQL API", "Sentry"),
        status = "Pre-Launch",
        detailImages = listOf(Res.Images.AI_DESCRIPTIONS_1, Res.Images.AI_DESCRIPTIONS_2),
        features = listOf(
            ProductFeature(
                icon = "🤖",
                title = "AI-Powered Generation",
                description = "Uses OpenAI GPT-4o-mini with JSON-mode prompt engineering to generate HTML descriptions, SEO titles (60 chars), and meta descriptions (155 chars) in a single API call."
            ),
            ProductFeature(
                icon = "🎨",
                title = "5 Tones & 3 Lengths",
                description = "Professional, Casual, Playful, Luxury, or Minimalist tone. Short, Medium, or Long length. Plus custom brand keywords and voice notes for fine-grained control."
            ),
            ProductFeature(
                icon = "⚡",
                title = "Resilient API Client",
                description = "Custom OpenAI client with 4 retries, exponential backoff with jitter, 90s total budget, 30s per-attempt timeout. Safe error messages prevent prompt leakage to merchants."
            ),
            ProductFeature(
                icon = "💰",
                title = "Cost Tracking",
                description = "Tracks prompt_tokens, completion_tokens, and computes USD cost per generation using OpenAI's pricing constants. Full generation history with applied status."
            ),
            ProductFeature(
                icon = "📊",
                title = "Monthly Quota System",
                description = "Free tier: 50 generations/month. Pro tier: 1,000/month. Quota auto-resets on calendar month change. Visual progress indicator with logistic curve simulation."
            ),
            ProductFeature(
                icon = "📋",
                title = "One-Click Apply",
                description = "Generated content is applied directly to Shopify via productUpdate GraphQL mutation with automatic retry on throttles. No copy-paste needed."
            )
        )
    ),
    InventoryAlerts(
        productName = "Inventory Alerts",
        tagline = "Proactive Multi-Location Inventory Monitoring",
        description = "Never miss a stock-out with real-time email alerts across all locations.",
        detailedDescription = "Inventory Alerts is a Shopify app that watches per-variant stock levels across multiple locations and sends branded email notifications via Resend when thresholds are crossed. Features a sophisticated alert state machine with escalation logic (ok → low → critical → out_of_stock) and debouncing to prevent spam. Includes per-alert error isolation (a failure in one alert doesn't block others), complete audit trail with delivery status, and webhook-driven instant re-evaluation when inventory levels change.",
        link = "https://github.com/MohamedElgohary88",
        imageRes = Res.Images.INVENTORY_ALERTS,
        techStack = listOf("Remix", "TypeScript", "React", "Polaris", "Prisma", "PostgreSQL", "Resend API", "Shopify GraphQL API", "Sentry"),
        status = "Pre-Launch",
        detailImages = listOf(Res.Images.INVENTORY_ALERTS_1, Res.Images.INVENTORY_ALERTS_2),
        features = listOf(
            ProductFeature(
                icon = "🔔",
                title = "Real-Time Email Alerts",
                description = "Branded HTML email notifications via Resend when stock crosses thresholds. Different colors and emojis for each alert level: Low (🟡), Critical (🔴), Out of Stock (⚫), Recovered (🟢)."
            ),
            ProductFeature(
                icon = "🏪",
                title = "Multi-Location Support",
                description = "Monitor inventory at a specific location or sum across all locations. Full cursor-pagination for locations, inventory levels, and product variants with safety cap."
            ),
            ProductFeature(
                icon = "🧠",
                title = "Smart State Machine",
                description = "Escalation matrix prevents alert spam: triggers only on level changes (ok→low→critical→out), not repeated same-level events. Tracks levelOrder for accurate state transitions."
            ),
            ProductFeature(
                icon = "🛡️",
                title = "Per-Alert Error Isolation",
                description = "If one alert's re-evaluation fails, the error is logged and processing continues to the next alert. One bad alert never blocks critical OOS notifications for other products."
            ),
            ProductFeature(
                icon = "📊",
                title = "Complete Audit Trail",
                description = "Full event history of every threshold crossing with email delivery status. Acknowledge tracking and searchable/filterable alert management dashboard."
            ),
            ProductFeature(
                icon = "⚡",
                title = "Instant Webhook Evaluation",
                description = "Re-evaluates all alerts when inventory_levels/update webhooks fire. Products/update webhook also refreshes cached product titles, variant names, and SKUs."
            )
        )
    ),
    ReturnsAutomation(
        productName = "Returns Automation",
        tagline = "Self-Service Return Portal & Admin Dashboard",
        description = "Customers return items in seconds. You approve in one click. Full lifecycle management.",
        detailedDescription = "Returns Automation is a Shopify app with a public customer-facing return portal and a full admin dashboard for processing returns. Customers look up their order by number + email, select items to return, choose a resolution (Refund/Exchange/Store Credit), and submit — all without contacting support. Features a security-conscious portal design with rate limiting (in-memory token bucket), strict input validation to prevent GraphQL injection, an inventory ledger system that prevents double-claiming, and a return state machine with automatic timestamp tracking per milestone. RMA number generation uses a clean alphabet (no I/O/0/1).",
        link = "https://github.com/MohamedElgohary88",
        imageRes = Res.Images.RETURNS_AUTOMATION,
        techStack = listOf("Remix", "TypeScript", "React", "Polaris", "Prisma", "PostgreSQL", "Resend API", "Shopify GraphQL API", "Sentry"),
        status = "Pre-Launch",
        detailImages = listOf(Res.Images.RETURNS_AUTOMATION_1, Res.Images.RETURNS_AUTOMATION_2),
        features = listOf(
            ProductFeature(
                icon = "🌐",
                title = "Customer Return Portal",
                description = "Shareable, unauthenticated portal with branded UI. Customers enter order number + email to look up their order, select items to return with quantity, choose resolution, and submit."
            ),
            ProductFeature(
                icon = "🛡️",
                title = "Security-First Design",
                description = "In-memory token bucket rate limiting (10 lookups/5min per IP+slug). Strict input validation with regexes before GraphQL queries. Generic error messages to prevent order enumeration."
            ),
            ProductFeature(
                icon = "📋",
                title = "Inventory Ledger System",
                description = "Before accepting a return, builds a per-line-item ledger: total ordered - already fulfilled - already refunded - previously claimed. Prevents double-claiming of return quantities."
            ),
            ProductFeature(
                icon = "📊",
                title = "Admin Returns Dashboard",
                description = "Full list/filter/search of return requests with status badges, detail view, and status transition controls (Approve, Reject, Mark Received, Mark Refunded/Exchanged)."
            ),
            ProductFeature(
                icon = "🔢",
                title = "RMA Number Generation",
                description = "Short, human-friendly RMA numbers (e.g., RMA-A3B9K7X2) using a clean alphabet. Auto-tags Shopify orders with return-requested for easy identification."
            ),
            ProductFeature(
                icon = "📧",
                title = "Email Notifications",
                description = "Sends status-update emails to customers and new-return notifications to merchants via Resend. Branded HTML templates with status-specific colors."
            )
        )
    )
}
